/**
 * MCP Host Status Card Component
 * Displays MCP Host connection status and manages tool synchronization
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { mcpHostService, MCPHostStatus, MCPConnectionStatus } from '../../services/mcp-host';
import { externalToolsService } from '../../services/external-tools';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Server,
  Calendar,
  Wrench,
  Play,
  Settings,
} from 'lucide-react';

const MCPHostStatusCard: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<MCPHostStatus>({
    status: 'not_connected',
    host: '127.0.0.1',
    port: 8000,
  });
  const [mcpConnectionStatus, setMCPConnectionStatus] = useState<MCPConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuthStore();
  const { toast } = useToast();

  // Check connection on mount with proper error handling for new users
  useEffect(() => {
    checkMCPHostConnection();
    checkRealMCPStatus();
    
    // Reduced polling frequency - check every 30 seconds instead of continuous
    const interval = setInterval(() => {
      checkMCPHostConnection(true);
      checkRealMCPStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const checkMCPHostConnection = useCallback(async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    
    try {
      const connectionStatus = await mcpHostService.checkConnection();
      setStatus(connectionStatus);
      
      if (!silent && connectionStatus.status === 'connected') {
        toast({
          title: "Tools Service Connected",
          description: `Connected to Tools Service v${connectionStatus.version}`,
        });
      }
    } catch (error) {
      console.error('Failed to check MCP Host connection:', error);
      setStatus({
        status: 'not_connected',
        host: '127.0.0.1',
        port: 8000,
        error: 'Cannot connect to backend',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const checkRealMCPStatus = useCallback(async () => {
    try {
      const realStatus = await mcpHostService.getMCPConnectionStatus();
      setMCPConnectionStatus(realStatus);
    } catch (error) {
      // Handle 400 errors gracefully - likely means new user with no tools
      console.log('MCP status check (expected for new users):', error);
      setMCPConnectionStatus({
        isRunning: false,
        connectedTools: [], // Empty tools array for new users
        overallStatus: 'disconnected',
        lastUpdated: new Date(),
        error: null // Don't treat as error for new users
      });
    }
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      checkMCPHostConnection(),
      checkRealMCPStatus()
    ]);
    setIsRefreshing(false);
  };

  const handleSyncTools = async () => {
    if (!user?.id) {
      toast({
        title: "Not authenticated",
        description: "Please log in to sync tools",
        variant: "destructive",
      });
      return;
    }

    setIsSyncing(true);

    try {
      // Use the same service as the Tools page
      const result = await externalToolsService.syncWithMCPHost(user.id);

      if (result.success) {
        toast({
          title: "Tools Synchronized",
          description: result.message,
        });
        // Refresh connection to get updated tool count
        await checkMCPHostConnection(true);
        await checkRealMCPStatus();
      } else {
        toast({
          title: "Sync Failed",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Sync Error",
        description: "Failed to synchronize tools",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleStartMCPHost = async () => {
    setIsLoading(true);
    
    try {
      const result = await mcpHostService.startMCPHost();
      
      if (result.success) {
        toast({
          title: "Starting Tools Service",
          description: result.message,
        });
        
        // Wait a bit then check connection
        setTimeout(() => {
          checkMCPHostConnection();
        }, 3000);
      } else {
        toast({
          title: "Failed to Start",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Start Error",
        description: "Failed to start Tools Service",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = () => {
    // Use real MCP status if available, otherwise fall back to legacy status
    const realStatus = mcpConnectionStatus?.overallStatus;
    const displayStatus = realStatus || status.status;
    
    switch (displayStatus) {
      case 'connected':
        return {
          icon: CheckCircle,
          label: 'Connected',
          description: mcpConnectionStatus ? 
            `${mcpConnectionStatus.connectedTools.filter(t => t.enabled && t.status === 'connected').length} tools connected` :
            'Tools Service is running and ready',
          variant: 'default' as const,
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
          borderColor: 'border-green-200'
        };
      case 'partial':
        return {
          icon: AlertCircle,
          label: 'Partial Connection',
          description: mcpConnectionStatus ? 
            `${mcpConnectionStatus.connectedTools.filter(t => t.enabled && t.status === 'connected').length} of ${mcpConnectionStatus.connectedTools.filter(t => t.enabled).length} tools connected` :
            'Some tools are not connected',
          variant: 'secondary' as const,
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-700',
          borderColor: 'border-yellow-200'
        };
      case 'connecting':
        return {
          icon: Loader2,
          label: 'Connecting',
          description: 'Attempting to connect to Tools Service',
          variant: 'secondary' as const,
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200'
        };
      case 'error':
        return {
          icon: AlertCircle,
          label: 'Error',
          description: mcpConnectionStatus?.error || status.error || 'Tools connection error',
          variant: 'destructive' as const,
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
          borderColor: 'border-red-200'
        };
      default:
        // For new users with no tools (400 error case)
        return {
          icon: Settings,
          label: 'No Tools Added',
          description: 'No tools configured yet',
          variant: 'secondary' as const,
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const formatLastConnected = (date: Date | null | undefined) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card className={cn(
      "transition-all duration-200 hover:shadow-md",
      statusConfig.borderColor
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <StatusIcon className={cn(
              "h-5 w-5",
              status.status === 'connecting' && "animate-spin",
              statusConfig.textColor
            )} />
            <CardTitle className="text-lg">Tools Service</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant={statusConfig.variant}>
              {statusConfig.label}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading || isRefreshing}
              className="h-6 w-6 p-0"
            >
              {(isLoading || isRefreshing) ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
        <CardDescription>
          {statusConfig.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Only show error display for actual connection issues, not new user scenarios */}
        {((status.error && status.error.includes('Cannot connect to backend') && !mcpConnectionStatus) || 
          (mcpConnectionStatus?.error && mcpConnectionStatus.overallStatus === 'error')) && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-orange-800 font-medium">Connection Issue</p>
                <p className="text-sm text-orange-700">{mcpConnectionStatus?.error || status.error}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* MCP Tools Status Display */}
        {mcpConnectionStatus && mcpConnectionStatus.connectedTools.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Tools Status:</span>
            </div>
            <div className="space-y-2">
              {mcpConnectionStatus.connectedTools.filter(tool => tool.enabled).map((tool) => (
                <div key={tool.name} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">{tool.name}</span>
                    {tool.transport_type && (
                      <span className="text-xs text-muted-foreground">({tool.transport_type})</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      variant={tool.status === 'connected' ? 'default' : 
                              tool.status === 'error' ? 'destructive' : 'secondary'}
                      className="text-xs"
                    >
                      {tool.status === 'connected' ? 'Connected' : 
                       tool.status === 'error' ? 'Error' : 'Disconnected'}
                    </Badge>
                    {tool.status === 'connected' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {mcpConnectionStatus.connectedTools.filter(tool => !tool.enabled).length > 0 && (
              <p className="text-xs text-muted-foreground">
                {mcpConnectionStatus.connectedTools.filter(tool => !tool.enabled).length} tools disabled
              </p>
            )}
          </div>
        )}

        {/* Connection Details */}
        {status.status === 'connected' && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center space-x-2">
                <Server className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Host:</span>
                <span className="text-sm font-medium">{status.host}:{status.port}</span>
              </div>
              
              {status.version && (
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Version:</span>
                  <span className="text-sm font-medium">{status.version}</span>
                </div>
              )}

              {mcpConnectionStatus && (
                <div className="flex items-center space-x-2">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Tools:</span>
                  <span className="text-sm font-medium">
                    {mcpConnectionStatus.connectedTools.filter(t => t.enabled && t.status === 'connected').length} connected / {mcpConnectionStatus.connectedTools.filter(t => t.enabled).length} enabled
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last Connected */}
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Last connected:</span>
          <span className="text-sm font-medium">
            {formatLastConnected(status.lastConnected)}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2 pt-2">
          {status.status === 'not_connected' && (
            <Button
              onClick={handleStartMCPHost}
              disabled={isLoading}
              className="w-full"
              size="sm"
              variant="outline"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting Tools Service...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start Tools Service
                </>
              )}
            </Button>
          )}

          {/* Show Add Tools button when no tools are configured (MCP connection status null or 0 tools) */}
          {(!mcpConnectionStatus || mcpConnectionStatus.connectedTools.filter(t => t.enabled).length === 0) && (
            <Button
              onClick={() => navigate('/external-tools')}
              className="w-full"
              size="sm"
              variant="default"
            >
              <Settings className="h-4 w-4 mr-2" />
              Add Tools
            </Button>
          )}

          {status.status === 'connected' && mcpConnectionStatus && mcpConnectionStatus.connectedTools.filter(t => t.enabled).length > 0 && (
            <Button
              onClick={handleSyncTools}
              disabled={isSyncing || !user}
              className="w-full"
              size="sm"
              variant="default"
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Synchronizing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Tools
                </>
              )}
            </Button>
          )}
        </div>

        {/* Info Note */}
        {status.status === 'not_connected' && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              Tools Service manages external tool integrations. Start it to enable tool functionality in AI chat.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MCPHostStatusCard;